import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { Affix, Card, Checkbox, Divider, Flex } from 'antd-v5';
import debounce from 'lodash/debounce';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DatePicker, Text } from 'scouting-ui/lib';
import { TextArea } from 'scouting-ui/lib/Input';

import BulkEntryRequirement from '@modules/bulkEntry/components/BulkEntryRequirement';
import { RequirementsHeader } from '@modules/bulkEntry/components/RequirementsHeader/RequirementsHeader';
import {
  isNotUpdateableRequirement,
  sortBulkRequirements,
} from '@modules/bulkEntry/duck/utils/requirementUtils';
import { mbcApi } from '@modules/meritBadgeCounselors/api';
import {
  useGetMeritBadgeRequirementsQuery,
  usePostBulkYouthMeritBadgeRequirementsMutation,
} from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';

import {
  canSubmitSel,
  completionDateSel,
  counselorApprovedSel,
  selectedRequirementsSetSel,
  selectedVersionSel,
  submitPayloadSel,
} from '../duck/selectors';
import { bulkEntryActions } from '../duck/slice';
import { parseSubmitError } from '../duck/utils';
import {
  MBCConfirmationModal,
  type YouthSubmitResult,
} from './MBCConfirmationModal';
import styles from './RequirementsCard.module.scss';

interface RequirementsCardProps {
  meritBadgeId: number;
  meritBadgeName: string;
  meritBadgeImage: string;
  memberId: number | string;
}

function RequirementsCard(props: RequirementsCardProps) {
  const { meritBadgeId, meritBadgeName, meritBadgeImage, memberId } = props;
  const dispatch = useDispatch();

  const selectedVersion = useSelector(selectedVersionSel);
  const selectedReqsSet = useSelector(selectedRequirementsSetSel);
  const canSubmit = useSelector(canSubmitSel);
  const completionDate = useSelector(completionDateSel);
  const counselorApproved = useSelector(counselorApprovedSel);
  const submitPayload = useSelector(submitPayloadSel);

  const [localComment, setLocalComment] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [submitResults, setSubmitResults] = useState<YouthSubmitResult[]>([]);
  const [isAffixed, setIsAffixed] = useState(false);

  const versionId = selectedVersion
    ? Number(selectedVersion.versionId)
    : undefined;

  const { data: requirementsInfo, isLoading: isLoadingReqs } =
    useGetMeritBadgeRequirementsQuery(
      { meritBadgeId, params: { versionId } },
      { skip: !meritBadgeId || !versionId },
    );

  const [postRequirements, { isLoading: isSubmitting }] =
    usePostBulkYouthMeritBadgeRequirementsMutation();

  const sortedRequirements = useMemo(() => {
    if (!requirementsInfo?.requirements) return [];
    return sortBulkRequirements(requirementsInfo.requirements);
  }, [requirementsInfo?.requirements]);

  const updateableReqIds = useMemo(
    () =>
      sortedRequirements
        .filter(r => !isNotUpdateableRequirement(r))
        .map(r => Number(r.id)),
    [sortedRequirements],
  );

  const allSelected =
    updateableReqIds.length > 0 &&
    updateableReqIds.every(id => selectedReqsSet.has(id));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetComment = useCallback(
    debounce((value: string) => {
      dispatch(bulkEntryActions.setComment(value));
    }, 300),
    [],
  );

  function handleCommentChange(ev: ChangeEvent<HTMLTextAreaElement>) {
    const value = ev.target.value;
    setLocalComment(value);
    debouncedSetComment(value);
  }

  function handleToggleRequirement(id: number, toggle: boolean) {
    dispatch(bulkEntryActions.toggleRequirement({ requirementId: id, toggle }));
  }

  function handleToggleAll(checked: boolean) {
    dispatch(
      bulkEntryActions.toggleAllRequirements({
        toggle: checked,
        requirementIds: updateableReqIds,
      }),
    );
  }

  async function handleSubmit() {
    if (submitPayload.length === 0) return;

    setSubmitResults([]);

    try {
      await postRequirements({
        meritBadgeId,
        payload: submitPayload,
      }).unwrap();

      toastService.success(
        intl.formatMessage({ id: 'page.mbcBulkEntry.toasts.success' }),
      );

      dispatch(bulkEntryActions.resetAfterSubmit());
      setLocalComment('');
      setShowConfirmationModal(false);

      dispatch(
        mbcApi.util.invalidateTags([
          tagFactory.MBCDashboard.userDashboard(memberId),
        ]),
      );
    } catch (error) {
      const parsedResults = parseSubmitError(error);
      if (parsedResults) {
        setSubmitResults(parsedResults);
        return;
      }

      toastService.error(
        intl.formatMessage({ id: 'page.mbcBulkEntry.toasts.error' }),
      );
    }
  }

  function handleOpenModal() {
    setSubmitResults([]);
    setShowConfirmationModal(true);
  }

  function handleCloseModal() {
    setShowConfirmationModal(false);
  }

  const canOpenModal = canSubmit;

  return (
    <Card>
      <MBCConfirmationModal
        show={showConfirmationModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        requirements={requirementsInfo?.requirements || []}
        isLoading={isSubmitting}
        disableSubmit={!canSubmit}
        submitResults={submitResults}
      />

      <RequirementsHeader
        title={<FormattedMessage id="page.mbcBulkEntry.requirements.title" />}
        image={meritBadgeImage}
        name={meritBadgeName}
        isLoading={isLoadingReqs}
      />

      {!isLoadingReqs && sortedRequirements.length > 0 && (
        <>
          <Divider className={styles.divider} />

          <Text font="body-body-2">
            <FormattedMessage id="page.mbcBulkEntry.requirements.subtitle" />
          </Text>

          <div className={styles.selectAllWrapper}>
            <Checkbox
              checked={allSelected}
              indeterminate={
                !allSelected &&
                updateableReqIds.some(id => selectedReqsSet.has(id))
              }
              onChange={e => handleToggleAll(e.target.checked)}
            >
              <FormattedMessage id="page.mbcBulkEntry.requirements.selectAll" />
            </Checkbox>
          </div>

          <div>
            {sortedRequirements.map(req => (
              <BulkEntryRequirement
                key={req.id}
                requirement={req}
                checked={selectedReqsSet.has(Number(req.id))}
                onChange={handleToggleRequirement}
                isLoading={isSubmitting}
                disable={isNotUpdateableRequirement(req)}
              />
            ))}
          </div>

          <Divider className={styles.divider} />

          <Affix offsetBottom={0} onChange={affixed => setIsAffixed(!!affixed)}>
            <Flex
              vertical
              gap="small"
              className={`${styles.stickyFooter} ${
                isAffixed ? styles.stickyFooterAffixed : ''
              }`}
            >
              <TextArea
                placeholder="Comment (optional)"
                autoSize={{ minRows: 1 }}
                maxLength={150}
                onChange={handleCommentChange}
                value={localComment}
              />
              <Flex justify="flex-end" align="center" gap="middle">
                <Checkbox
                  checked={counselorApproved}
                  onChange={e =>
                    dispatch(
                      bulkEntryActions.setCounselorApproved(e.target.checked),
                    )
                  }
                >
                  <FormattedMessage id="page.mbcBulkEntry.requirements.counselorApproved" />
                </Checkbox>
                <DatePicker
                  className={styles.datePicker}
                  onChange={date => {
                    dispatch(
                      bulkEntryActions.setCompletionDate(
                        date ? date.toJSDate() : undefined,
                      ),
                    );
                  }}
                  value={
                    completionDate
                      ? DateTime.fromJSDate(completionDate)
                      : undefined
                  }
                  placeholder={intl.formatMessage({
                    id: 'bulkEntry.approveInfo.completionDate',
                  })}
                  format="MM/dd/yyyy"
                  disabledDate={date => date > DateTime.now()}
                  allowClear
                />
                <Button
                  onClick={handleOpenModal}
                  disabled={!canOpenModal}
                  loading={isSubmitting}
                >
                  <FormattedMessage id="page.mbcBulkEntry.requirements.apply" />
                </Button>
              </Flex>
            </Flex>
          </Affix>
        </>
      )}
    </Card>
  );
}

export function RequirementsCardGuard(props: RequirementsCardProps) {
  return <RequirementsCard {...props} />;
}
